import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import { LocalStorage } from '../../shared/services/local-storage.service';
import { ClientToServerEvents, ServerToClientEvents } from './socket.model';
import { RoleAccessService } from '../../shared/services/role-access.service';

@Injectable({
  providedIn: 'root',
})
export class SocketService {
  private readonly localStorage = inject(LocalStorage);
  private readonly roleAccessService = inject(RoleAccessService);
  private readonly destroyRef = inject(DestroyRef);

  private socket: Socket<ServerToClientEvents, ClientToServerEvents> | null = null;

  readonly connectionStatus = signal<'connected' | 'disconnected' | 'reconnecting'>('disconnected');

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.disconnect();
    });
  }

  connectForSubAdmin(): void {
    if (!this.roleAccessService.isSubAdmin()) {
      this.disconnect();
      return;
    }

    const socket = this.ensureSocket();
    const token = this.localStorage.getToken() || '';
    socket.io.opts.query = { token };
    if (!socket.connected) {
      socket.connect();
    }
  }

  disconnect(): void {
    if (!this.socket) {
      this.connectionStatus.set('disconnected');
      return;
    }

    this.socket.removeAllListeners();
    this.socket.io.removeAllListeners();
    this.socket.disconnect();
    this.socket = null;
    this.connectionStatus.set('disconnected');
  }

  /**
   * Listens for an event from the server.
   * @template Ev The event name.
   * @returns An observable that emits the event data when the event is received.
   */
  on<Ev extends keyof ServerToClientEvents>(
    event: Ev
  ): Observable<Parameters<ServerToClientEvents[Ev]>[0]> {
    return new Observable((subscriber) => {
      const socket = this.ensureSocket();
      const handler = (data: Parameters<ServerToClientEvents[Ev]>[0]) => {
        subscriber.next(data);
      };

      // Socket.io types merge reserved events with user events; use a narrow event-emitter view here.
      const rawSocket = socket as unknown as {
        on: (ev: string, fn: (...args: unknown[]) => void) => void;
        off: (ev: string, fn: (...args: unknown[]) => void) => void;
      };

      rawSocket.on(event as string, handler as (...args: unknown[]) => void);

      return () => {
        rawSocket.off(event as string, handler as (...args: unknown[]) => void);
      };
    });
  }

  /**
   * Emits an event to the server.
   * @template Ev The event name.
   * @param event The event name.
   * @param ...args The event arguments.
   */
  emit<Ev extends keyof ClientToServerEvents>(
    event: Ev,
    ...args: Parameters<ClientToServerEvents[Ev]>
  ): void {
    this.ensureSocket().emit(event, ...args);
  }

  /**
   * Binds connection events to the socket. These events are emitted by the socket when the connection status changes.
   * The events are as follows:
   * - 'connect': The socket has connected to the server.
   * - 'disconnect': The socket has disconnected from the server.
   * - 'reconnect_attempt': The socket is attempting to reconnect to the server.
   * - 'reconnect': The socket has reconnected to the server.
   * - 'reconnect_failed': The socket has failed to reconnect to the server.
   * - 'reconnect_error': The socket has encountered an error while attempting to reconnect to the server.
   */
  private bindConnectionEvents(): void {
    const socket = this.ensureSocket();
    socket.on('connect', () => this.connectionStatus.set('connected'));
    socket.on('disconnect', () => this.connectionStatus.set('disconnected'));

    // Reconnect events are emitted from the Manager instance.
    socket.io.on('reconnect_attempt', () => {
      this.connectionStatus.set('reconnecting');
      // Refresh token before each reconnect attempt.
      const token = this.localStorage.getToken() || '';
      socket.io.opts.query = { token };
    });
    socket.io.on('reconnect', () => this.connectionStatus.set('connected'));
    socket.io.on('reconnect_failed', () => this.connectionStatus.set('disconnected'));
    socket.io.on('reconnect_error', () => this.connectionStatus.set('reconnecting'));
  }

  private ensureSocket(): Socket<ServerToClientEvents, ClientToServerEvents> {
    if (this.socket) {
      return this.socket;
    }

    const token = this.localStorage.getToken() || '';
    this.socket = io(environment.SOCKET_BASE_URL, {
      query: { token },
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 16000,
    });

    this.bindConnectionEvents();
    return this.socket;
  }
}
